import { useEffect, useRef, useState } from "react"
import styles from "./Window.module.css"
import { X, Minus, ArrowsOut } from "phosphor-react"
import { SOUNDS } from "../../constants/sounds"
import { useSound } from "../../hooks/useSound"

type ResizeDirection = "right" | "bottom" | "corner" | "left" | "top" | "topLeft" | "topRight" | "bottomLeft"

type WindowProps = {
  title: string
  onClose: () => void
  isMinimized: boolean
  onToggleMinimize: () => void
  initialPosition?: { x: number; y: number }
  initialSize?: { width: number; height: number }
  minSize?: { width: number; height: number }
  children: React.ReactNode
  bodyStyle?: React.CSSProperties
  bodyClassName?: string
  containerClassName?: string
}

const ANIMATION_DURATIONS = {
  OPENING: 250,
  CLOSING: 300,
  MINIMIZING: 400
} as const

const DEFAULT_SIZE = { width: 800, height: 500 }
const DEFAULT_MIN_SIZE = { width: 400, height: 240 }

export default function Window({
  title,
  onClose,
  isMinimized,
  onToggleMinimize,
  initialSize = DEFAULT_SIZE,
  minSize = DEFAULT_MIN_SIZE,
  children,
  bodyStyle,
  bodyClassName,
  containerClassName
}: WindowProps) {
  const computeCenter = (w: number, h: number) => ({
    x: Math.max(0, (window.innerWidth - w) / 2),
    y: Math.max(0, (window.innerHeight - h) / 2)
  })

  const [size, setSize] = useState(initialSize)
  const [position, setPosition] = useState(() => computeCenter(initialSize.width, initialSize.height))
  const [isMaximized, setIsMaximized] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isMinimizingAnim, setIsMinimizingAnim] = useState(false)
  const [isOpening, setIsOpening] = useState(true)
  const positionRef = useRef(position)
  const sizeRef = useRef(size)
  const prevPositionRef = useRef(position)
  const prevSizeRef = useRef(size)
  const dragging = useRef(false)
  const resizing = useRef(false)
  const resizeDirection = useRef<ResizeDirection | null>(null)
  const offset = useRef({ x: 0, y: 0 })

  const playClose = useSound(SOUNDS.CLICK_CLOSE, { volume: 0.5 })
  const playMinimize = useSound(SOUNDS.CLICK_MINIMIZE, { volume: 0.5 })
  const playResizeMaximize = useSound(SOUNDS.CLICK_RESIZE_MAXIMIZE, { volume: 0.5 })
  const playResizeMinimize = useSound(SOUNDS.CLICK_RESIZE_MINIMIZE, { volume: 0.5 })

  useEffect(() => {
    const timer = setTimeout(() => setIsOpening(false), ANIMATION_DURATIONS.OPENING)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => { positionRef.current = position }, [position])
  useEffect(() => { sizeRef.current = size }, [size])

  useEffect(() => {
    setPosition(computeCenter(size.width, size.height))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (isMaximized) return
    dragging.current = true
    offset.current = { x: e.clientX - positionRef.current.x, y: e.clientY - positionRef.current.y }
  }

  const onMouseDownResize = (e: React.MouseEvent, dir: ResizeDirection) => {
    if (isMaximized) return
    e.preventDefault()
    e.stopPropagation()
    resizing.current = true
    resizeDirection.current = dir
    offset.current = { x: e.clientX, y: e.clientY }
  }

  const onMouseMove = (e: MouseEvent) => {
    if (dragging.current) setPosition({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y })
    if (resizing.current && resizeDirection.current) {
      const dx = e.clientX - offset.current.x
      const dy = e.clientY - offset.current.y
      offset.current = { x: e.clientX, y: e.clientY }
      let { width, height } = sizeRef.current
      let { x, y } = positionRef.current
      switch (resizeDirection.current) {
        case "right": width = Math.max(minSize.width, width + dx); break
        case "left": if (width - dx >= minSize.width) { width -= dx; x += dx } break
        case "bottom": height = Math.max(minSize.height, height + dy); break
        case "top": if (height - dy >= minSize.height) { height -= dy; y += dy } break
        case "corner": width = Math.max(minSize.width, width + dx); height = Math.max(minSize.height, height + dy); break
        case "topLeft": if (width - dx >= minSize.width) { width -= dx; x += dx } if (height - dy >= minSize.height) { height -= dy; y += dy } break
        case "topRight": width = Math.max(minSize.width, width + dx); if (height - dy >= minSize.height) { height -= dy; y += dy } break
        case "bottomLeft": if (width - dx >= minSize.width) { width -= dx; x += dx } height = Math.max(minSize.height, height + dy); break
      }
      setSize({ width, height })
      setPosition({ x, y })
    }
  }

  const onMouseUp = () => {
    dragging.current = false
    resizing.current = false
    resizeDirection.current = null
  }

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
    return () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    playClose()
    setIsClosing(true)
    setTimeout(onClose, ANIMATION_DURATIONS.CLOSING)
  }

  const handleMinimize = () => {
    playMinimize()
    setIsMinimizingAnim(true)
    setTimeout(() => {
      onToggleMinimize()
      setIsMinimizingAnim(false)
    }, ANIMATION_DURATIONS.MINIMIZING)
  }

  const toggleMaximize = () => {
    if (!isMaximized) {
      playResizeMaximize()
      prevPositionRef.current = positionRef.current
      prevSizeRef.current = sizeRef.current
      setIsMaximized(true)
      setPosition({ x: 0, y: 0 })
      setSize({ width: window.innerWidth, height: window.innerHeight })
    } else {
      playResizeMinimize()
      setIsMaximized(false)
      setPosition(prevPositionRef.current)
      setSize(prevSizeRef.current)
    }
  }

  if (isMinimized) return null

  return (
    <div
      className={`${styles.window} ${isMaximized ? styles.maximized : ""} ${isOpening ? styles.opening : ""} ${isClosing ? styles.closing : ""} ${isMinimizingAnim ? styles.minimizing : ""} ${containerClassName || ""}`}
      style={{
        ...(isMaximized ? {} : { top: position.y, left: position.x, width: size.width, height: size.height }),
        '--window-top': `${position.y}px`
      } as React.CSSProperties}
    >
      <div className={styles.header} onMouseDown={onMouseDownHeader}>
        <div className={styles.buttons}>
          <span className={styles.red} onClick={handleClose}><X size={10} color="#6C130D" weight="bold" className={styles.icon} /></span>
          <span className={styles.yellow} onClick={handleMinimize}><Minus size={10} color="#8D591C" weight="bold" className={styles.icon} /></span>
          <span className={styles.green} onClick={toggleMaximize}><ArrowsOut size={10} color="#2B6017" weight="bold" className={styles.icon} /></span>
        </div>
        <span className={styles.title}>{title}</span>
      </div>
      <div className={`${styles.body} ${bodyClassName || ""}`} style={bodyStyle}>{children}</div>
      {!isMaximized && (
        <>
          <div className={styles.resizerRight} onMouseDown={e => onMouseDownResize(e, "right")} />
          <div className={styles.resizerLeft} onMouseDown={e => onMouseDownResize(e, "left")} />
          <div className={styles.resizerTop} onMouseDown={e => onMouseDownResize(e, "top")} />
          <div className={styles.resizerBottom} onMouseDown={e => onMouseDownResize(e, "bottom")} />
          <div className={styles.resizerCorner} onMouseDown={e => onMouseDownResize(e, "corner")} />
          <div className={styles.resizerTopLeft} onMouseDown={e => onMouseDownResize(e, "topLeft")} />
          <div className={styles.resizerTopRight} onMouseDown={e => onMouseDownResize(e, "topRight")} />
          <div className={styles.resizerBottomLeft} onMouseDown={e => onMouseDownResize(e, "bottomLeft")} />
        </>
      )}
    </div>
  )
}
